/**
 * Smooth trails. Positions arrive tens of seconds apart (the free feeds
 * allow few requests), so a plane that curved between two fixes would be drawn
 * as a straight chord with a corner at every point. Each fix also carries the
 * aircraft's heading, which says which way the path was heading when it passed
 * through — so between two fixes we draw the cubic Bézier that leaves the first
 * along its heading and arrives at the second along its heading. That is how a
 * constant-rate turn actually looks, and it reduces to the straight line when
 * the headings agree with the chord.
 *
 * It only ever interpolates *between* fixes the aircraft really reported.
 * Where headings are missing, or disagree wildly with the chord (holding
 * patterns, noisy fixes), the leg is left straight rather than inventing a loop.
 */

export interface PathPoint {
  lat: number;
  lon: number;
  ts: number;
  track?: number | null;
}

/** Shorter legs than this are already smooth at any useful zoom. */
const MIN_LEG_KM = 1;
/** Legs longer than this in time are coverage gaps, not curves. */
const MAX_LEG_MS = 5 * 60_000;
/** A heading more than this far from the chord means the path isn't one smooth arc. */
const MAX_HEADING_OFF_CHORD_DEG = 70;
/** Bézier handle length as a fraction of the chord (⅓ for a straight leg, 0.39 for a quarter circle). */
const HANDLE = 0.36;
const KM_PER_VERTEX = 1;
const MAX_VERTICES_PER_LEG = 14;

const RAD = Math.PI / 180;

function angleOff(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/**
 * Intermediate [lon, lat] vertices for the leg a→b (endpoints excluded), or an
 * empty array when the leg should stay a straight line.
 */
export function curveBetween(a: PathPoint, b: PathPoint): [number, number][] {
  if (a.track == null || b.track == null) return [];
  if (b.ts - a.ts > MAX_LEG_MS || b.ts <= a.ts) return [];
  const kmPerDegLon = 111.32 * Math.cos(((a.lat + b.lat) / 2) * RAD);
  const x = (b.lon - a.lon) * kmPerDegLon; // east, km
  const y = (b.lat - a.lat) * 110.57; // north, km
  const d = Math.hypot(x, y);
  if (d < MIN_LEG_KM) return [];
  const chord = ((Math.atan2(x, y) / RAD) + 360) % 360;
  if (angleOff(a.track, chord) > MAX_HEADING_OFF_CHORD_DEG || angleOff(b.track, chord) > MAX_HEADING_OFF_CHORD_DEG) return [];

  const L = d * HANDLE;
  const p1x = Math.sin(a.track * RAD) * L;
  const p1y = Math.cos(a.track * RAD) * L;
  const p2x = x - Math.sin(b.track * RAD) * L;
  const p2y = y - Math.cos(b.track * RAD) * L;

  const n = Math.min(Math.max(Math.ceil(d / KM_PER_VERTEX), 2), MAX_VERTICES_PER_LEG);
  const out: [number, number][] = [];
  for (let i = 1; i < n; i++) {
    const t = i / n;
    const u = 1 - t;
    const bx = 3 * u * u * t * p1x + 3 * u * t * t * p2x + t * t * t * x;
    const by = 3 * u * u * t * p1y + 3 * u * t * t * p2y + t * t * t * y;
    out.push([a.lon + bx / kmPerDegLon, a.lat + by / 110.57]);
  }
  return out;
}

/** Vertices still allowed in one redraw — keeps thousands of trails from swamping the map. */
export interface SmoothBudget {
  left: number;
}

/** The leg as a coordinate list: `[a, …curve…, b]` while the budget lasts, else just `[a, b]`. */
export function legCoordinates(a: PathPoint, b: PathPoint, budget: SmoothBudget): [number, number][] {
  const start: [number, number] = [a.lon, a.lat];
  const end: [number, number] = [b.lon, b.lat];
  if (budget.left <= 0) return [start, end];
  const mids = curveBetween(a, b);
  budget.left -= mids.length;
  return [start, ...mids, end];
}
